/**
 * The fleet's DQHealth reports, as the admin screen reads them.
 *
 * `GET /admin/dqhealth` on qv-data, guarded by the `admin` role exactly as the
 * rest of `api/admin.ts` is. **Not the machine-level `/dqhealth.json`**, which
 * is private-network only because it names every file on a volume — a browser
 * cannot reach 6PN, and this serves what the collector already stored.
 *
 * Reads degrade, `api/admin.ts`'s rule: no server, a failed fetch or a refusal
 * is an empty list, and the screen says it has nothing rather than erroring.
 * There is nothing to write here at all — the report is produced by a job.
 */

import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL);

/** Whether there is a server to ask at all. */
export const dqHealthConfigured: boolean = BASE !== "";

/** Which schedule produced a report: the hourly beat, or a nightly pass. */
export type ReportKind = "rth" | "eod";

/** One verdict, and the date it was judged on. */
export interface Verdict {
  level: "ok" | "stale" | "ahead" | "absent" | "missing" | "na" | "notTrading" | "none";
  asOf: string | null;
  expected: string | null;
  note?: string;
}

export interface FileRow {
  path: string;
  registered: boolean;
  rule: string | null;
  base: { bytes?: number | null; written?: string | null; ageSeconds?: number | null;
          column?: string | null; lastDate?: string | null; error?: string } | null;
  served: { lastDate?: string | null; symbols?: Record<string, string | null> };
  verdict: Verdict;
  symbolVerdicts?: Record<string, Verdict>;
}

export interface SymbolRow {
  symbol: string;
  served: string | null;
  verdict: Verdict;
}

export interface MachineRow {
  app: string;
  id: string | null;
  state: string | null;
  region: string | null;
  probed?: boolean;
  wokenForThisReport?: boolean;
  seconds?: number;
  levels?: Record<string, number>;
  mismatches?: Array<{ path: string; symbol: string | null; writer: string; theirs: string }>;
  report?: {
    error?: string;
    expectedSession?: string | null;
    machine?: { role?: string; pid?: number };
    files?: FileRow[];
    symbolFiles?: SymbolRow[];
  };
}

export interface FleetReport {
  generatedAt: string;
  kind: ReportKind;
  market: string;
  trigger: string | null;
  writerApp: string;
  watchlist: string[];
  random: string[];
  expectedSession: string | null;
  machines: MachineRow[];
  sources?: {
    bars?: Record<string, Record<string, unknown>>;
    fundamentals?: Record<string, Record<string, unknown>>;
  };
  summary: Record<string, number | boolean | string[]>;
  elapsedSeconds: number;
}

export interface StoredReport {
  id: number;
  generatedAt: string;
  trigger: string | null;
  report: FleetReport;
}

/** The newest `limit` reports of one kind — the first is Rpt 1. */
export async function fetchReports(
  kind: ReportKind,
  market = "US",
  limit = 2,
): Promise<StoredReport[]> {
  if (!BASE) return [];
  try {
    const response = await fetch(
      `${BASE}/admin/dqhealth?kind=${kind}&market=${market}&limit=${limit}`,
      { headers: await requestHeaders() },
    );
    if (!response.ok) return [];
    const body = (await response.json()) as { reports?: StoredReport[] };
    return body.reports ?? [];
  } catch {
    return [];
  }
}
